
/**
 * Utilities for detecting and working with Phantom wallet
 */

/**
 * Detects if we're in Phantom's in-app browser
 */
export const isPhantomInAppBrowser = (): boolean => {
  return (
    typeof window !== 'undefined' && 
    window?.solana?.isPhantom && 
    (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    window?.solana?.isInAppBrowser)
  );
};

/**
 * Detects if we're specifically in Phantom's mobile app browser (not extension)
 */
export const isPhantomMobileApp = (): boolean => {
  return (
    isPhantomInAppBrowser() &&
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  );
};

/**
 * Detects if we're in iOS Phantom App specifically
 */
export const isPhantomIOS = (): boolean => {
  return (
    isPhantomMobileApp() &&
    /iPhone|iPad|iPod/i.test(navigator.userAgent)
  );
};

/**
 * Detects if we're in Android Phantom App specifically
 */
export const isPhantomAndroid = (): boolean => {
  return (
    isPhantomMobileApp() &&
    /Android/i.test(navigator.userAgent)
  );
};

/**
 * Detects the Phantom app version based on available APIs
 * Returns: 'legacy', 'current', or 'unknown'
 */
export const getPhantomAppVersion = (): 'legacy' | 'current' | 'unknown' => {
  if (!isPhantomMobileApp() || !window?.solana) {
    return 'unknown';
  }
  
  // Check for newer version API features
  if (typeof window.solana.closeWindow === 'function') {
    return 'current';
  }
  
  return 'legacy';
};

/**
 * Tries to connect directly to Phantom's in-app browser
 * Returns true if successfully connected, false otherwise
 */
export const connectToPhantomInApp = async (): Promise<boolean> => {
  if (isPhantomInAppBrowser() && window?.solana) {
    try {
      await window.solana.connect();
      return window?.solana?.isConnected || false;
    } catch (error) {
      console.error("Error connecting to Phantom in-app:", error);
      return false;
    }
  }
  return false;
};

/**
 * Tries to disconnect directly from Phantom's in-app browser
 * Returns true if successfully disconnected
 */
export const disconnectFromPhantomInApp = async (): Promise<boolean> => {
  if (isPhantomInAppBrowser() && window?.solana?.isConnected) {
    try {
      // First, disconnect from Phantom
      await window.solana.disconnect();
      console.log("Disconnected from Phantom successfully");
      
      // If we're in Phantom mobile app specifically, try to close the window
      if (isPhantomMobileApp()) {
        console.log("Attempting to close Phantom mobile window...");
        
        // Start trying to close the window with a staggered approach
        setTimeout(() => {
          tryToClosePhantomWindow();
        }, 300);
      }
      
      return true;
    } catch (error) {
      console.error("Error disconnecting from Phantom in-app:", error);
      return false;
    }
  }
  return false;
};

/**
 * Helper function to try multiple window closing mechanisms
 * with proper logging for debugging
 */
const tryToClosePhantomWindow = (): void => {
  if (!isPhantomMobileApp()) return;
  
  const phantomVersion = getPhantomAppVersion();
  console.log(`Trying to close Phantom window (detected version: ${phantomVersion})...`);
  
  // Try method 1: Direct API call (newer versions)
  if (window.solana && typeof window.solana.closeWindow === 'function') {
    try {
      console.log("Attempting closeWindow API call...");
      window.solana.closeWindow();
      return; // If successful, stop here
    } catch (error) {
      console.log("closeWindow API failed:", error);
    }
  }
  
  // Try method 2: postMessage to parent (some versions)
  try {
    console.log("Attempting postMessage approach...");
    window.parent.postMessage({ method: 'disconnect' }, '*');
    setTimeout(() => window.parent.postMessage({ method: 'closeWindow' }, '*'), 100);
  } catch (error) {
    console.log("postMessage approach failed:", error);
  }
  
  // Try method 3: Try to use history navigation (fallback)
  try {
    if (window.history && window.history.length > 1) {
      console.log("Attempting history.back()...");
      window.history.back();
    }
  } catch (error) {
    console.log("history.back() failed:", error);
  }
  
  // Schedule a second attempt with delay
  setTimeout(() => {
    forceClosePhantomWindow();
  }, 500);
};

/**
 * Last resort function to attempt to close the Phantom mobile window
 * This is a separate function to be called after disconnect if window is still open
 */
export const forceClosePhantomWindow = (): void => {
  if (!isPhantomMobileApp()) return;
  
  console.log("Force closing Phantom mobile window...");
  
  // Try multiple approaches to close the window
  try {
    // Try direct close if available (newer versions)
    if (window.solana && window.solana.closeWindow) {
      console.log("Trying direct closeWindow()...");
      window.solana.closeWindow();
    }
    
    // Try using postMessage with different message formats
    console.log("Trying multiple postMessage formats...");
    window.parent.postMessage({ method: 'closeWindow' }, '*');
    window.parent.postMessage('closeWindow', '*');
    window.parent.postMessage({ action: 'close' }, '*');
    
    // Try browser back functionality as last resort
    if (window.history && window.history.length > 1) {
      console.log("Trying history.back()...");
      window.history.back();
    }
    
    // For iOS-specific approach
    if (isPhantomIOS()) {
      console.log("Trying iOS-specific approaches...");
      // Try to use a link that might trigger app switching
      const a = document.createElement('a');
      a.href = 'about:blank';
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
    
    // For Android-specific approach
    if (isPhantomAndroid()) {
      console.log("Trying Android-specific approaches...");
      // Some Android webviews respond to this
      if (window.navigator && window.navigator.app) {
        // @ts-ignore - Non-standard API
        window.navigator.app.exitApp();
      }
    }
    
    // Try deep linking as a last resort
    console.log("Attempting deep link fallback...");
    window.location.href = 'phantom://close';
    
  } catch (error) {
    console.error("All window closing attempts failed:", error);
  }
};

/**
 * Determines if an error is a user rejection error
 */
export const isUserRejectionError = (error: any): boolean => {
  return !!(error?.message && (
    error.message.includes('User rejected') || 
    error.message.includes('user rejected') ||
    error.message.includes('declined')
  ));
};

/**
 * Format wallet address to show shortened version
 */
export const formatWalletAddress = (address: string): string => {
  if (!address) return '';
  return `${address.slice(0, 4)}...${address.slice(-4)}`;
};
